import { zodResolver } from "@hookform/resolvers/zod";
import { FC, useCallback, useEffect, useRef } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { z } from "@/i18n";
import { useFormStore } from "../form-store";

import { StandartFile } from "../common";
import { APISchemas, PATCH, POST } from "@/api";
import { Box, useToast } from "@chakra-ui/react";
import { handleError } from "./_handle-errors";
import isEqual from "lodash.isequal";
import { formUpdateMS } from "../contant";
import { TelegramFormPresent } from "../forms-presents/telegram-form-present";
import { useAuthStore } from "@/context/auth-store/auth-store";
import {
  checkVideoSize,
  checkGeneralImageFormat,
  checkMPEGVideoFormat,
  checkImageSize,
  checkVideoAspectRatio,
  checkTelegramContentSize,
} from "../../Post/platform-validations";
import { i18n } from "@/i18n";

export type telegramSchema = z.infer<typeof telegramSchema>;
// eslint-disable-next-line react-refresh/only-export-components
export const telegramSchema = z
  .object({
    id: z.number().optional(),
    content: z.string().optional(),
    files: StandartFile.array().max(10).optional(),
  })
  .refine(checkTelegramContentSize, {
    path: ["root"],
    message: i18n.t("post-validations.telegram.content-length"),
  })
  .refine((o) => checkImageSize(o, 10 * 1024 * 1024), {
    path: ["root"],
    message: i18n.t("post-validations.telegram.image-size"),
  })
  .refine((o) => checkVideoSize(o, 50 * 1024 * 1024), {
    path: ["root"],
    message: i18n.t("post-validations.telegram.video-size"),
  })
  .refine(checkMPEGVideoFormat, {
    path: ["root"],
    message: i18n.t("post-validations.telegram.video-format"),
  })
  .refine(checkGeneralImageFormat, {
    path: ["root"],
    message: i18n.t("post-validations.telegram.image-format"),
  })
  .refine((o) => checkVideoAspectRatio(o, 0, 20), {
    path: ["root"],
    message: i18n.t("post-validations.telegram.video-aspectRatio"),
  })
  .refine((o) => o.content || (o.files && o.files.length > 0), {
    path: ["root"],
    message: i18n.t("post-validations.telegram.has-content"),
  });

export const TelegramFormContainer: FC<{
  channel: APISchemas["Channel"] & { hasError?: boolean };
  defaultValue?: telegramSchema;
}> = ({ channel, defaultValue }) => {
  const toast = useToast();
  const defaultValueRef = useRef<typeof defaultValue>();
  const activeOrganization = useAuthStore((f) => f.activeOrganization);

  const formSync = useFormStore((f) => f.formSync);

  const [formState, setFormState] = useFormStore((f) => [
    f.fromStates[channel.id.toString()] ?? {
      sync: true,
    },
    f.setFormState,
  ]);

  const {
    main,
    addTelegramPost,
    scheduledAt,
    subscribe,
    unsubscribe,
    setForm,
    setProgressBlock,
    goToStep,
    setPlatformHasError,
    setGlobalError,
  } = useFormStore((s) => ({
    main: s.mainForms.main,
    setForm: s.setForm,
    subscribe: s.subscribe,
    unsubscribe: s.unsubscribe,
    scheduledAt: s.scheduledAt,
    addTelegramPost: s.addTelegramPost,
    setProgressBlock: s.setProgressBlock,
    setPlatformHasError: s.setPlatformHasError,
    goToStep: s.goToStep,
    setGlobalError: s.setGlobalError,
  }));

  const methods = useForm<telegramSchema>({
    resolver: zodResolver(telegramSchema),
  });
  const {
    getValues,
    trigger,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = methods;

  if (!isEqual(defaultValue, defaultValueRef.current)) {
    reset(defaultValue);
    setFormState(channel.id.toString(), {
      sync: false,
    });
    defaultValueRef.current = defaultValue;
  }

  const draftForm = useCallback(async () => {
    const result = await trigger();

    if (!result) {
      setPlatformHasError(channel, true);
      return;
    }

    setPlatformHasError(channel, false);
    setForm(channel.id, getValues());
  }, [channel, getValues, setForm, setPlatformHasError, trigger]);

  const onSubmit: (
    status: APISchemas["StatusCf9Enum"]
  ) => SubmitHandler<telegramSchema> = useCallback(
    (status) => async (data) => {
      if (!activeOrganization) {
        return;
      }
      const coverUrls: Record<string, string> = {};
      data.files?.forEach((file) => {
        if (!file.data.video) {
          return;
        }
        if (!("coverUrl" in file.data.video)) {
          return;
        }
        if (
          !file.data.video.coverUrl ||
          typeof file.data.video.coverUrl !== "string"
        ) {
          return;
        }
        coverUrls[file.data.video.id] = file.data.video.coverUrl;
      });

      const body = {
        body: data.content,
        channel: channel.id,
        status,
        scheduled_at: scheduledAt.toISOString(),
        media: data.files
          ?.map((f) => f.data.image?.id || f.data.video?.id)
          .filter((m): m is string => !!m)
          .map((m) => Number(m)),
        metadata: isEqual(coverUrls, {})
          ? undefined
          : {
              cover_url: coverUrls,
            },
      };

      const { data: post, error } = defaultValueRef.current?.id
        ? await PATCH("/telegram/{org_pk}/post/{id}/", {
            params: {
              path: {
                org_pk: activeOrganization.id.toString(),
                id: defaultValueRef.current.id,
              },
            },
            body,
          })
        : await POST("/telegram/{org_pk}/post/", {
            params: {
              path: {
                org_pk: activeOrganization.id.toString(),
              },
            },
            body,
          });

      if (error) {
        setProgressBlock(true);
        handleError(channel.id.toString(), error, setGlobalError);
        setPlatformHasError(channel, true);
        goToStep?.(1);
        return;
      }

      if (!post) {
        toast({
          status: "error",
          title: i18n.t("errors.unexpected-error-contact-customer-service"),
        });
        return;
      }

      setPlatformHasError(channel, false);
      addTelegramPost(post);
    },
    [
      activeOrganization,
      addTelegramPost,
      channel,
      goToStep,
      scheduledAt,
      setGlobalError,
      setPlatformHasError,
      setProgressBlock,
      toast,
    ]
  );

  useEffect(() => {
    if (main && formState.sync) {
      reset({
        content: main.content,
        files: main.files,
      });
    }
  }, [formState.sync, main, reset]);

  useEffect(() => {
    if (isDirty) {
      setFormState(channel.id.toString(), {
        sync: false,
      });
    }
  }, [channel.id, isDirty, setFormState]);

  useEffect(() => {
    const submitFn = handleSubmit(onSubmit("planned"));
    const draftFn = handleSubmit(onSubmit("draft"));
    subscribe("submit", submitFn);
    subscribe("check", draftForm);
    subscribe("draft", draftFn);

    return () => {
      unsubscribe("submit", submitFn);
      unsubscribe("check", draftForm);
      unsubscribe("draft", draftFn);
    };
  }, [handleSubmit, onSubmit, draftForm, subscribe, unsubscribe]);

  useEffect(() => {
    if (!formSync) {
      return;
    }

    let prev: telegramSchema = {};

    const interval = setInterval(() => {
      const values = getValues();

      if (isEqual(prev, values)) {
        return;
      }

      if (channel.hasError) {
        draftForm();
      }

      setForm(channel.id, values);
      prev = values;
    }, formUpdateMS);

    return () => {
      clearInterval(interval);
    };
  }, [channel, draftForm, formSync, getValues, setForm]);

  return (
    <Box border="1px solid #E2E4E6" rounded="md" px="3" pos="relative">
      <form>
        <FormProvider {...methods}>
          <TelegramFormPresent channel={channel} />
        </FormProvider>
      </form>
    </Box>
  );
};
